import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useLibrary } from '../context/LibraryContext';
import { usePlayer } from '../context/PlayerContext';
import { FaPlay, FaTrash, FaPlus, FaRandom, FaSort, FaMusic, FaArrowLeft } from 'react-icons/fa';
import '../styles/PlaylistDetail.css';

const PlaylistDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { songs, playlists, removeSongFromPlaylist, deletePlaylist, loading, error } = useLibrary();
  const { playSong, playPlaylist } = usePlayer();
  
  const [playlist, setPlaylist] = useState(null);
  const [playlistSongs, setPlaylistSongs] = useState([]);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [availableSongs, setAvailableSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSmartOptions, setShowSmartOptions] = useState(false);
  const [smartOptions, setSmartOptions] = useState({
    transitionType: 'tempo',
    sortBy: 'ascending'
  });

  useEffect(() => {
    // Find the playlist in the context
    const foundPlaylist = playlists.find(p => p._id === id);
    if (foundPlaylist) {
      setPlaylist(foundPlaylist);
      
      // Get full song objects for the playlist
      if (songs.length > 0 && foundPlaylist.songs && foundPlaylist.songs.length > 0) {
        // Convertir les IDs en chaînes pour la comparaison
        const playlistSongIds = foundPlaylist.songs.map(id => id.toString());
        
        // Récupérer les objets de chansons complets
        const fullSongs = [];
        for (const songId of playlistSongIds) {
          const song = songs.find(s => s._id.toString() === songId);
          if (song) fullSongs.push(song);
        }
        
        setPlaylistSongs(fullSongs);
        
        // Set available songs (songs not in the playlist)
        const songsNotInPlaylist = songs.filter(
          song => !playlistSongIds.includes(song._id.toString())
        );
        setAvailableSongs(songsNotInPlaylist);
      }
    } else {
      // If playlist not found in context, fetch it directly
      fetchPlaylist();
    }
  }, [id, playlists, songs]);

  const fetchPlaylist = async () => {
    try {
      // Récupérer la playlist
      const playlistRes = await api.get(`/playlists/${id}`);
      setPlaylist(playlistRes.data);
      
      // Récupérer toutes les chansons disponibles
      const allSongsRes = await api.get('/songs');
      const allSongs = allSongsRes.data;
      
      // Récupérer les chansons de la playlist
      if (playlistRes.data.songs && playlistRes.data.songs.length > 0) {
        // Convertir les IDs en chaînes pour la comparaison
        const playlistSongIds = playlistRes.data.songs.map(id => id.toString());
        
        // Récupérer les objets de chansons complets pour la playlist
        const playlistSongs = allSongs.filter(song => 
          playlistSongIds.includes(song._id.toString())
        );
        
        setPlaylistSongs(playlistSongs);
        
        // Définir les chansons disponibles (non présentes dans la playlist)
        const availableSongs = allSongs.filter(song => 
          !playlistSongIds.includes(song._id.toString())
        );
        
        setAvailableSongs(availableSongs);
      } else {
        // Si la playlist est vide, toutes les chansons sont disponibles
        setPlaylistSongs([]);
        setAvailableSongs(allSongs);
      }
    } catch (err) {
      console.error('Error fetching playlist:', err);
    }
  };

  const handlePlaySong = (song, index) => {
    playSong(song, playlistSongs, index);
  };

  const handlePlayAll = () => {
    if (playlistSongs.length > 0) {
      // Passer directement le tableau de chansons de la playlist
      playPlaylist(playlistSongs);
    }
  };

  const handleRemoveSong = async (songId) => {
    try {
      await removeSongFromPlaylist(playlist._id, songId);
      setPlaylistSongs(playlistSongs.filter(song => song._id !== songId));
    } catch (err) {
      console.error('Error removing song from playlist:', err);
    }
  };

  const handleAddSong = async (songId) => {
    try {
      console.log('Adding song to playlist:', songId, 'to playlist:', playlist._id);
      const res = await api.put(`/playlists/${playlist._id}/add-song/${songId}`);
      
      // Update the playlist in state
      setPlaylist(res.data);
      
      // Find the song to add from available songs
      const songToAdd = availableSongs.find(s => s._id === songId);
      if (songToAdd) {
        // Add the song to playlistSongs
        setPlaylistSongs([...playlistSongs, songToAdd]);
        
        // Remove from available songs
        setAvailableSongs(availableSongs.filter(s => s._id !== songId));
      } else {
        // If we can't find the song in available songs, refresh the playlist
        fetchPlaylist();
      }
    } catch (err) {
      console.error('Error adding song to playlist:', err);
      alert('Erreur lors de l\'ajout du morceau à la playlist');
    }
  };

  const handleDeletePlaylist = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette playlist ?')) {
      try {
        await deletePlaylist(playlist._id);
        navigate('/playlists');
      } catch (err) {
        console.error('Error deleting playlist:', err);
      }
    }
  };

  const handleGenerateSmartPlaylist = async () => {
    try {
      const songIds = playlistSongs.map(song => song._id);
      console.log('Smart Mix - Sending song IDs:', songIds);
      console.log('Smart Mix - Options:', {
        transitionType: smartOptions.transitionType,
        sortBy: smartOptions.sortBy
      });
      
      // Utiliser l'instance API avec la route correcte
      const res = await api.post('/analysis/smart-playlist', {
        songIds,
        transitionType: smartOptions.transitionType,
        sortBy: smartOptions.sortBy
      });
      
      console.log('Réponse reçue du serveur:', res.data);
      
      // Update the playlist songs order
      const orderedSongs = res.data.map(songId => 
        playlistSongs.find(song => song._id === songId)
      ).filter(song => song !== undefined);
      
      console.log('Chansons réordonnées:', orderedSongs);
      setPlaylistSongs(orderedSongs);
      
      // Save the new order to the playlist
      await api.put(`/playlists/${playlist._id}/reorder`, {
        songIds: orderedSongs.map(song => song._id)
      });
      
      setShowSmartOptions(false);
    } catch (err) {
      console.error('Error generating smart playlist:', err);
    }
  };

  const filteredAvailableSongs = searchTerm
    ? availableSongs.filter(song => 
        song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableSongs;

  if (loading) {
    return <div className="loading">Chargement de la playlist...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!playlist) {
    return <div className="error-message">Playlist non trouvée</div>;
  }

  return (
    <div className="playlist-detail-page">
      <div className="back-link" onClick={() => navigate('/playlists')}>
        <FaArrowLeft /> Retour aux playlists
      </div>
      
      <div className="playlist-header">
        <div className="playlist-info">
          <h1>{playlist.name}</h1>
          <p className="playlist-description">{playlist.description}</p>
          <div className="playlist-meta">
            <span>{playlistSongs.length} morceaux</span>
            {playlistSongs.length > 0 && (
              <span>
                {Math.floor(playlistSongs.reduce((acc, song) => acc + (song.duration || 0), 0) / 60)} min
              </span>
            )}
          </div>
        </div>
        
        <div className="playlist-actions">
          <button 
            className="btn-play-all"
            onClick={handlePlayAll}
            disabled={playlistSongs.length === 0}
          >
            <FaPlay /> Tout lire
          </button>
          <button 
            className="btn-smart-playlist"
            onClick={() => setShowSmartOptions(!showSmartOptions)}
            disabled={playlistSongs.length < 2}
          >
            <FaRandom /> Smart Mix
          </button>
          <button 
            className="btn-add-songs"
            onClick={() => setShowAddSongModal(true)}
          >
            <FaPlus /> Ajouter
          </button>
          <button 
            className="btn-delete-playlist"
            onClick={handleDeletePlaylist}
          >
            <FaTrash /> Supprimer
          </button>
        </div>
      </div>
      
      {showSmartOptions && (
        <div className="smart-options">
          <h3>Options de mix intelligent</h3>
          <div className="smart-options-form">
            <div className="form-group">
              <label htmlFor="transitionType">Type de transition</label>
              <select
                id="transitionType"
                value={smartOptions.transitionType}
                onChange={(e) => setSmartOptions({...smartOptions, transitionType: e.target.value})}
              >
                <option value="tempo">Progression de tempo</option>
                <option value="key">Compatibilité de tonalité</option>
                <option value="energy">Évolution d'énergie</option>
                <option value="genre">Cohérence de genre</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="sortBy">Direction</label>
              <select
                id="sortBy"
                value={smartOptions.sortBy}
                onChange={(e) => setSmartOptions({...smartOptions, sortBy: e.target.value})}
              >
                <option value="ascending">Croissant</option>
                <option value="descending">Décroissant</option>
                <option value="wave">Ondulation</option>
              </select>
            </div>
            
            <button 
              className="btn-generate"
              onClick={handleGenerateSmartPlaylist}
            >
              Générer
            </button>
          </div>
        </div>
      )}
      
      {playlistSongs.length === 0 ? (
        <div className="empty-playlist">
          <FaMusic className="empty-icon" />
          <p>Cette playlist est vide</p>
          <button 
            className="btn-add-songs"
            onClick={() => setShowAddSongModal(true)}
          >
            Ajouter des morceaux
          </button>
        </div>
      ) : (
        <div className="songs-table-container">
          <table className="songs-table">
            <thead>
              <tr>
                <th></th>
                <th>Titre</th>
                <th>Artiste</th>
                <th>BPM</th>
                <th>Tonalité</th>
                <th>Genre</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {playlistSongs.map((song, index) => (
                <tr key={song._id} className="song-row">
                  <td>
                    <button 
                      className="btn-play" 
                      onClick={() => handlePlaySong(song, index)}
                      aria-label="Jouer"
                    >
                      <FaPlay />
                    </button>
                  </td>
                  <td>{song.title}</td>
                  <td>{song.artist}</td>
                  <td>{song.bpm}</td>
                  <td>{song.key}</td>
                  <td>{song.genre}</td>
                  <td>
                    <button 
                      className="btn-remove" 
                      onClick={() => handleRemoveSong(song._id)}
                      aria-label="Retirer de la playlist"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Add Songs Modal */}
      {showAddSongModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Ajouter des morceaux</h2>
              <button 
                className="btn-close"
                onClick={() => setShowAddSongModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-content">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Rechercher par titre ou artiste..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {availableSongs.length === 0 ? (
                <div className="no-songs">
                  <p>Tous vos morceaux sont déjà dans cette playlist</p>
                </div>
              ) : filteredAvailableSongs.length === 0 ? (
                <div className="no-songs">
                  <p>Aucun morceau ne correspond à votre recherche</p>
                </div>
              ) : (
                <div className="available-songs-list">
                  {filteredAvailableSongs.map(song => (
                    <div key={song._id} className="available-song-item">
                      <div className="song-info">
                        <div className="song-title">{song.title}</div>
                        <div className="song-artist">{song.artist}</div>
                      </div>
                      <button 
                        className="btn-add" 
                        onClick={() => handleAddSong(song._id)}
                        aria-label="Ajouter à la playlist"
                      >
                        <FaPlus />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-done"
                onClick={() => setShowAddSongModal(false)}
              >
                Terminé
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistDetail;
